import React, { useCallback, useEffect, useState } from "react";
import { Button, Icon, ModalDialog, TextField, Success, PlainButton, Tooltip } from "@culturehq/components";

import { useProfile } from "app/context";
import { t, Trans } from "app/utils";

import LanguageSelector from "components/LanguageSelector";
import StoriesSlider from "components/stories/StoriesSlider";

import tags from "constants/tagsTypes";
import ChqModalDialog from "components/common/ChqModalDialog";
import { Story } from "chq";
import { SelectValue, SelectValueWithCategory } from "@culturehq/components/dist/components/form/typings";
import DisplayStories from "./DisplayStories";
import StoryFilterBar from "./StoryFilterBar";

interface EmbedSingleStoryProps {
  onClose: () => void;
  orgName: string;
  stories: Story[];
}

const categories = {
  [tags.STORIES]: "st",
  [tags.INTERESTS]: "i",
  [tags.DEPARTMENTS]: "d",
  [tags.PROFESSIONS]: "d",
  [tags.LOCATIONS]: "l",
  [tags.SKILLS]: "s",
  [tags.COMPANIES]: "co",
  [tags.CLUBS]: "cl",
  [tags.CLASS_YEARS]: "cy",
  [tags.PROGRAMS]: "p",
  [tags.DEGREES]: "de",
  [tags.ORG_VALUES]: "v",
  [tags.STORY_PROMPTS]: "sp",
  [tags.STORY_TRENDS]: "t",
  [tags.USERS]: "u"
};

const EMBBED_SLIDER_CODE = "<div id=\"IDENTIFIER\" /><script type=\"text/javascript\">+function (s, t, q) { (y = document.createElement(\"script\")).type = \"text/javascript\"; y.src = \"https://assets.culturehq.com/single-story-widget_1723572085.js\"; y.onload = function () { CHQ(s, t, q) }; document.getElementsByTagName(\"head\")[0].appendChild(y); }(\"#IDENTIFIER\", \"null\", FILTERS)</script>";

const replaceEmbbededCode = (
  filters: Record<string, any>
// eslint-disable-next-line max-len
) => EMBBED_SLIDER_CODE.replaceAll("IDENTIFIER", `chq_${Date.now()}`).replace("FILTERS", JSON.stringify(filters));

export const queryToOptions = (qs: Record<string, any>) => ({
  storyIds: qs[tags.STORIES] || null,
  interestIds: qs[tags.INTERESTS] || null,
  departmentIds: qs[tags.DEPARTMENTS] || null,
  locationIds: qs[tags.LOCATIONS] || null,
  skillIds: qs[tags.SKILLS] || null,
  companyIds: qs[tags.COMPANIES] || null,
  clubIds: qs[tags.CLUBS] || null,
  classYearIds: qs[tags.CLASS_YEARS] || null,
  programIds: qs[tags.PROGRAMS] || null,
  degreeIds: qs[tags.DEGREES] || null,
  organizationValueIds: qs[tags.ORG_VALUES] || null,
  storyPromptIds: qs[tags.STORY_PROMPTS] || null,
  trendIds: qs[tags.STORY_TRENDS] || null,
  userIds: qs[tags.USERS] || null,
  organizationId: qs.org || null
});

export const filtersToQuery = (filters: Record<any, any>[]) => {
  const options: Record<string, any> = {};
  filters.forEach(filter => {
    if (!categories[filter.category]) return;
    if (!options[categories[filter.category]]) {
      options[categories[filter.category]] = [];
    }
    options[categories[filter.category]].push(filter.value);
  });
  return options;
};

const EmbedSingleStory = ({
  onClose: onCloseMenu,
  orgName,
  stories
}: EmbedSingleStoryProps) => {
  const { profile } = useProfile();
  const [copied, setCopied] = useState(false);
  const [copiedStringId, setCopiedStringId] = useState(false);
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState<null | SelectValue | SelectValueWithCategory>("");
  const [selectedFilters, setSelectedFilters] = useState<any[]>([]);
  const [codedStoryData, setCodedStoryData] = useState<string>();
  const [embbedStories, setEmbbedStories] = useState<Story[]>([]);
  const [selectedStories, setSelectedStories] = useState<number[]>([]);

  const [embeddableCode, setEmbeddableCode] = useState<string>();
  const [externalHash] = useState(`#chq_${Date.now()}`);

  const getReplacebleQuery = () => {
    const query = filtersToQuery(selectedFilters);
    let replacebleQuery: Record<string, any> = {};
    if (language) replacebleQuery = { ...replacebleQuery, lang: language };
    if (selectedStories.length) {
      replacebleQuery = { ...replacebleQuery, [categories[tags.STORIES]]: selectedStories };
    } else {
      replacebleQuery = { ...replacebleQuery, ...query };
    }
    return { query, replacebleQuery };
  };

  const updateEmbeddableStory = () => {
    const { query, replacebleQuery } = getReplacebleQuery();
    const stringifyQuery = JSON.stringify(replacebleQuery);
    const base64Encoded = btoa(stringifyQuery);
    setCodedStoryData(base64Encoded);
    return { query, replacebleQuery };
  };

  useEffect(
    () => {
      setEmbbedStories(stories);
    }, [stories]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const elements = document.querySelectorAll(".copySringId, .copyCode");
      const isOutsideClick = Array.from(elements).every(
        element => !element.contains(event.target as Node)
      );
      if (isOutsideClick) {
        setCopied(false);
        setCopiedStringId(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(
    () => {
      updateEmbeddableStory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters]
  );

  useEffect(
    () => {
      const query = filtersToQuery(selectedFilters);
      let replacebleQuery = { ...query, org: profile.organization.id, lang: language };
      if (language) { replacebleQuery = { ...replacebleQuery }; }
      setEmbeddableCode(replaceEmbbededCode(replacebleQuery));
    }, [externalHash, language, profile, selectedFilters]
  );

  useEffect(
    () => {
      updateEmbeddableStory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, profile.organization.id, selectedStories]
  );

  const onOpen = useCallback(() => setOpen(true), [setOpen]);
  const onClose = useCallback(
    () => {
      setCopied(false);
      setCopiedStringId(false);
      setOpen(false);
      setSelectedFilters([]);
      setEmbbedStories(stories);
      if (onCloseMenu) {
        onCloseMenu();
      }
    },
    [onCloseMenu, setCopied, setCopiedStringId, setOpen, stories]
  );

  const copyToClipboard = (
    selector: string,
    setCopiedState: (copied: boolean) => void
  ) => {
    const element = document.querySelector(selector) as HTMLInputElement | null;
    if (element) {
      element.select();
      document.execCommand("copy");
      setCopiedState(true);
    }
  };

  const onCopy = useCallback(() => {
    copyToClipboard("#embeddable_slider_code", setCopied);
  }, []);

  const onCopyStringId = useCallback(() => {
    copyToClipboard("#embeddable_story_code", setCopiedStringId);
  }, []);

  const handleSelectedTags = (updatedOptions: any[]) => {
    setSelectedFilters(updatedOptions);
  };

  const handleClearSelectedOptions = () => {
    setSelectedFilters([]);
  };

  const handleSelectLanguage = (value: null | SelectValue | SelectValueWithCategory) => {
    setLanguage(value);
  };

  const onSelectSingleStory = (storyId: number) => {
    setSelectedStories(prev => (
      selectedStories.includes(storyId)
        ? prev.filter(id => id !== storyId)
        : [...prev, storyId]
    ));
  };

  return (
    <>
      <PlainButton onClick={onOpen}>
        <Icon icon="page" /><Trans>Single Story</Trans>
      </PlainButton>
      {open && (
        <ChqModalDialog onClose={onClose} className="EmbedStories">
          <ModalDialog.Heading onClose={onClose}>
            <Trans>Embed Single Story</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <DisplayStories selectedStories={embbedStories} />
            <hr className="row Separator" />
            <StoryFilterBar
              selectedOptions={selectedFilters}
              handleClearSelectedOptions={handleClearSelectedOptions}
              handleSelectedTags={handleSelectedTags}
            />
            <StoriesSlider
              admin
              language={language}
              orgName={orgName}
              stories={embbedStories}
              selectedStories={selectedStories}
              onSelectSingleStory={onSelectSingleStory}
              queryOptions={queryToOptions(filtersToQuery(selectedFilters))}
            />
            <div className="TipContainer">
              <Icon icon="lightbulb" />
              <div>
                <Trans>
                  <strong>Tip:</strong> Use this to display a responsive stories carousel right
                  on your site based on the tags selected above. Copy the code below and paste
                  it into a code block wherever you want it to display.
                </Trans>
              </div>
            </div>
            <span className="chq-ffd--sl--place">String ID</span>
            <TextField
              readOnly
              rows={3}
              name="embeddable_story_code"
              value={codedStoryData}
            >{null}
            </TextField>
            {copiedStringId && (
              <Success>
                <Trans>Embeddable code string id copied to the clipboard.</Trans>
              </Success>
            )}
            <span className="chq-ffd--sl--place">Embeddable Code</span>
            <TextField
              readOnly
              rows={3}
              name="embeddable_slider_code"
              value={embeddableCode}
            >
              <></>
            </TextField>
            {copied && (
              <Success>
                <Trans>Embeddable code copied to the clipboard.</Trans>
              </Success>
            )}
            <div className="ButtonContainer">
              <div className="LanguageSelector">
                <LanguageSelector onSelectLanguage={handleSelectLanguage} />
                <Tooltip tip={t("Any language you select will be the new default subtitle shown as long as it was created.")}>
                  <Icon icon="info" />
                </Tooltip>
              </div>
              <Button className="copySringId" primary onClick={onCopyStringId}>
                <Icon icon="copy" />
                <Trans>Copy String ID</Trans>
              </Button>
              <Button className="copyCode" primary onClick={onCopy}>
                <Icon icon="copy" />
                <Trans>Copy Code</Trans>
              </Button>
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default EmbedSingleStory;
